// Note: API base URL is handled by the global HeyGoodGame config
import hljs from 'highlight.js';

export interface DevToolbarConfig {
  enabled?: boolean;
  gameKey: string;
  position?: 'bottom-center' | 'bottom-left' | 'bottom-right';
  localStorageKeys?: string[];
  localStorageResetCallback?: () => void;
  experiments?: string[];
}

export interface ExperimentInfo {
  key: string;
  currentVariant?: string;
  availableVariants?: string[];
  status?: 'active' | 'inactive' | 'draft';
}

export interface DevToolbarAPI {
  init: (config: DevToolbarConfig) => void;
  show: () => void;
  hide: () => void;
  toggle: () => void;
  setExperimentVariants: (experimentKey: string, variants: string[]) => void;
  addLocalStorageKeys: (keys: string[]) => void;
}

class DevToolbar implements DevToolbarAPI {
  private config: Partial<DevToolbarConfig> = {};
  private isVisible = false;
  private isExpanded = false;
  private toolbarElement: HTMLElement | null = null;
  private experiments: ExperimentInfo[] = [];

  /**
   * Initialize the developer toolbar
   */
  public init(config: DevToolbarConfig) {
    if (!config.gameKey) {
      throw new Error('[DevToolbar] gameKey is required for initialization');
    }

    this.config = {
      enabled: false,
      position: 'bottom-center',
      localStorageKeys: [
        'heygg_ab_tests',
        'heygg_jwt', 
        'heygg_last_auth_check',
        'heygg_user',
        'heygg_anonymous_id'
      ],
      experiments: [],
      ...config,
    };

    // Only initialize in development mode
    if (!this.config.enabled) {
      return;
    }

    console.log(`[DevToolbar] Initializing developer toolbar for game: ${this.config.gameKey}`);
    this.createToolbar();
    this.loadExperiments();
  }

  /**
   * Show the toolbar
   */
  public show() {
    if (this.toolbarElement) {
      this.toolbarElement.style.display = 'block';
      this.isVisible = true;
    }
  }

  /**
   * Hide the toolbar
   */
  public hide() {
    if (this.toolbarElement) {
      this.toolbarElement.style.display = 'none';
      this.isVisible = false;
      this.isExpanded = false;
    }
  }

  /**
   * Toggle toolbar visibility
   */
  public toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Create the toolbar DOM structure
   */
  private createToolbar() {
    // Remove existing toolbar if it exists
    const existing = document.getElementById('heygg-dev-toolbar');
    if (existing) {
      existing.remove();
    }

    // Create toolbar container
    this.toolbarElement = document.createElement('div');
    this.toolbarElement.id = 'heygg-dev-toolbar';
    this.toolbarElement.innerHTML = this.getToolbarHTML();
    
    // Add styles
    this.addStyles();
    
    // Append to body
    document.body.appendChild(this.toolbarElement);
    
    // Add event listeners
    this.addEventListeners();
    
    // Update info panel
    this.updateInfoPanel();
    
    // Update storage table
    this.updateStorageTable();
  }

  /**
   * Get the toolbar HTML structure
   */
  private getToolbarHTML(): string {
    const positionClass = `heygg-dev-toolbar--${this.config.position}`;
    
    return `
      <div class="heygg-dev-toolbar ${positionClass}">
        <div class="heygg-dev-toolbar__toggle" id="heygg-dev-toolbar-toggle">
          <div class="heygg-dev-toolbar__logo">
            <svg class="heygg-dev-toolbar__logo-default" width="20" height="20" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#heygg-toolbar-clip0)">
              <g clip-path="url(#heygg-toolbar-clip1)">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M0.821533 7.23669L7.66169 5.40388L9.49453 12.2441L2.65436 14.0769L0.821533 7.23669ZM3.11259 15.787L4.94552 22.6276L11.7857 20.7948L9.95275 13.9542L3.11259 15.787ZM11.6629 13.496L13.4958 20.3366L20.3361 18.5037L18.5031 11.6631L11.6629 13.496ZM20.2132 11.2049L27.0538 9.37199L26.5956 7.66189L19.755 9.49481L17.9222 2.65459L24.763 0.821599L31.1781 24.7631L15.7869 28.8871L13.954 22.0467L29.345 17.9227L28.8868 16.2126L22.0462 18.0455L20.2132 11.2049ZM12.2439 22.5049L5.40374 24.3377L7.23663 31.1782L14.0768 29.3453L12.2439 22.5049ZM18.0449 9.95303L11.2046 11.7859L9.3718 4.94566L16.2121 3.11281L18.0449 9.95303Z" fill="currentColor"/>
              <rect x="5.40356" y="24.3376" width="7.08184" height="7.08184" transform="rotate(-15 5.40356 24.3376)" fill="#FF6741"/>
              <rect x="13.9539" y="22.0466" width="15.9339" height="7.08175" transform="rotate(-15 13.9539 22.0466)" fill="#FF6741"/>
              </g>
              </g>
              <defs>
              <clipPath id="heygg-toolbar-clip0">
              <rect width="32" height="32" fill="white"/>
              </clipPath>
              <clipPath id="heygg-toolbar-clip1">
              <rect width="24.786" height="24.786" fill="white" transform="translate(0.821533 7.23669) rotate(-15)"/>
              </clipPath>
              </defs>
            </svg>
            <svg class="heygg-dev-toolbar__logo-hover" width="20" height="20" viewBox="0 0 126 126" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#heygg-toolbar-hover-clip0)">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M0.910645 27.1526L28.8914 19.6552L36.3888 47.636L8.40808 55.1335L0.910645 27.1526ZM10.2825 62.1288L17.7802 90.1107L45.7609 82.6133L38.2632 54.6314L10.2825 62.1288ZM45.2585 52.757L52.7563 80.7389L80.7364 73.2416L73.2387 45.2597L45.2585 52.757ZM80.2341 43.3853L108.216 35.8875L106.342 28.8922L78.3597 36.39L70.8622 8.40918L98.8455 0.911087L125.087 98.8459L62.1283 115.716L54.6307 87.7342L117.589 70.8647L115.714 63.8694L87.7318 71.3672L80.2341 43.3853ZM47.6353 89.6086L19.6546 97.106L27.1522 125.087L55.1329 117.59L47.6353 89.6086ZM71.3643 38.2644L43.3841 45.7616L35.8867 17.7808L63.8669 10.2836L71.3643 38.2644Z" fill="white"/>
              <rect x="19.6541" y="97.1057" width="28.969" height="28.969" transform="rotate(-15 19.6541 97.1057)" fill="black"/>
              <rect x="54.6296" y="87.7339" width="65.1793" height="28.9686" transform="rotate(-15 54.6296 87.7339)" fill="black"/>
              </g>
              <defs>
              <clipPath id="heygg-toolbar-hover-clip0">
              <rect width="101.39" height="101.39" fill="white" transform="translate(0.910645 27.1526) rotate(-15)"/>
              </clipPath>
              </defs>
            </svg>
          </div>
          <span>Dev</span>
        </div>
        
        <div class="heygg-dev-toolbar__panel" id="heygg-dev-toolbar-panel">
          <div class="heygg-dev-toolbar__tabs">
            <button class="heygg-dev-toolbar__tab" data-tab="ab-tests">
              A/B Tests
            </button>
            <button class="heygg-dev-toolbar__tab" data-tab="storage">
              Storage
            </button>
            <button class="heygg-dev-toolbar__tab heygg-dev-toolbar__tab--active" data-tab="info">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
            </button>
          </div>
          
          <div class="heygg-dev-toolbar__content">
            <div class="heygg-dev-toolbar__tab-content" id="tab-ab-tests">
              <div class="heygg-dev-toolbar__section">
                <h4>Active Experiments</h4>
                <div id="experiments-list">
                  <div class="heygg-dev-toolbar__loading">Loading experiments...</div>
                </div>
              </div>
            </div>
            
            <div class="heygg-dev-toolbar__tab-content" id="tab-storage">
              <div class="heygg-dev-toolbar__section">
                <h4>Local Storage Keys</h4>
                <div class="heygg-dev-toolbar__storage-table" id="storage-keys-table">
                  <div class="heygg-dev-toolbar__loading">Loading storage keys...</div>
                </div>
              </div>
              <div class="heygg-dev-toolbar__section">
                <h4>Local Storage Reset</h4>
                <p>Clear development data and refresh the page</p>
                <button class="heygg-dev-toolbar__button heygg-dev-toolbar__button--danger" id="reset-storage-btn">
                  Reset & Refresh
                </button>
              </div>
            </div>
            
            <div class="heygg-dev-toolbar__tab-content heygg-dev-toolbar__tab-content--active" id="tab-info">
              <div class="heygg-dev-toolbar__section">
                <h4>Environment Info</h4>
                <div class="heygg-dev-toolbar__info-item">
                  <span class="heygg-dev-toolbar__info-label">API Base URL:</span>
                  <span class="heygg-dev-toolbar__info-value" id="api-base-url-value">Loading...</span>
                </div>
                <div class="heygg-dev-toolbar__info-item">
                  <span class="heygg-dev-toolbar__info-label">Auth Status:</span>
                  <span class="heygg-dev-toolbar__info-value" id="auth-status-value">Checking...</span>
                </div>
                <div class="heygg-dev-toolbar__info-item">
                  <span class="heygg-dev-toolbar__info-label">Hostname:</span>
                  <span class="heygg-dev-toolbar__info-value">${window.location.hostname}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Add CSS styles for the toolbar
   */
  private addStyles() {
    const existingStyles = document.getElementById('heygg-dev-toolbar-styles');
    if (existingStyles) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'heygg-dev-toolbar-styles';
    style.textContent = `
      /* Highlight.js GitHub theme (minimal) */
      .hljs {
        color: #24292e;
        background: #ffffff;
      }
      .hljs-doctag, .hljs-keyword, .hljs-meta .hljs-keyword, .hljs-template-tag, .hljs-template-variable, .hljs-type, .hljs-variable.language_ {
        color: #d73a49;
      }
      .hljs-title, .hljs-title.class_, .hljs-title.class_.inherited__, .hljs-title.function_ {
        color: #6f42c1;
      }
      .hljs-attr, .hljs-attribute, .hljs-literal, .hljs-meta, .hljs-number, .hljs-operator, .hljs-selector-attr, .hljs-selector-class, .hljs-selector-id, .hljs-variable {
        color: #005cc5;
      }
      .hljs-string, .hljs-meta .hljs-string {
        color: #032f62;
      }
      .hljs-comment {
        color: #6a737d;
      }
      .hljs-deletion {
        color: #b31d28;
      }
      .hljs-addition {
        color: #22863a;
      }

      .heygg-dev-toolbar {
        position: fixed;
        bottom: 20px;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        line-height: 1.4;
      }
      
      .heygg-dev-toolbar--bottom-center {
        left: 50%;
        transform: translateX(-50%);
      }
      
      .heygg-dev-toolbar--bottom-left {
        left: 20px;
      }
      
      .heygg-dev-toolbar--bottom-right {
        right: 20px;
      }
      
      .heygg-dev-toolbar__toggle {
        background: #150C09;
        color: white;
        border: 1px solid #FF6741;
        border-radius: 8px;
        padding: 8px 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        box-shadow: 0 4px 12px rgba(21, 12, 9, 0.4);
        transition: all 0.2s ease;
      }
      
      .heygg-dev-toolbar__logo {
        position: relative;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }
      
      .heygg-dev-toolbar__logo svg {
        position: absolute;
        top: 0;
        left: 0;
        width: 20px;
        height: 20px;
        transition: opacity 0.2s ease;
      }
      
      .heygg-dev-toolbar__logo-default {
        opacity: 1;
      }
      
      .heygg-dev-toolbar__logo-hover {
        opacity: 0;
      }
      
      .heygg-dev-toolbar__toggle:hover {
        background: #FF6741;
        border-color: #FF6741;
        box-shadow: 0 6px 16px rgba(255, 103, 65, 0.3);
      }
      
      .heygg-dev-toolbar__toggle:hover .heygg-dev-toolbar__logo-default {
        opacity: 0;
      }
      
      .heygg-dev-toolbar__toggle:hover .heygg-dev-toolbar__logo-hover {
        opacity: 1;
      }
      
      .heygg-dev-toolbar__panel {
        position: absolute;
        bottom: 60px;
        left: 50%;
        transform: translateX(-50%);
        background: white;
        border: 2px solid #150C09;
        border-radius: 12px;
        box-shadow: 0 8px 24px rgba(21, 12, 9, 0.25);
        width: min(600px, calc(100vw - 40px));
        max-height: 500px;
        overflow: hidden;
        display: none;
      }
      
      .heygg-dev-toolbar__panel--expanded {
        display: block;
      }
      
      .heygg-dev-toolbar__tabs {
        display: flex;
        border-bottom: 1px solid #e0e0e0;
        background: #f8f9fa;
      }
      
      .heygg-dev-toolbar__tab {
        flex: 1;
        padding: 12px 16px;
        border: none;
        background: none;
        cursor: pointer;
        font-weight: 500;
        color: #666;
        transition: all 0.2s ease;
      }
      
      .heygg-dev-toolbar__tab--active {
        color: #FF6741;
        background: white;
        border-bottom: 2px solid #FF6741;
      }
      
      .heygg-dev-toolbar__tab:hover:not(.heygg-dev-toolbar__tab--active) {
        background: #e9ecef;
      }
      
      .heygg-dev-toolbar__content {
        max-height: 420px;
        overflow-y: auto;
      }
      
      .heygg-dev-toolbar__tab-content {
        display: none;
        padding: 16px;
      }
      
      .heygg-dev-toolbar__tab-content--active {
        display: block;
      }
      
      .heygg-dev-toolbar__section {
        margin-bottom: 16px;
      }
      
      .heygg-dev-toolbar__section:last-child {
        margin-bottom: 0;
      }
      
      .heygg-dev-toolbar__section h4 {
        margin: 0 0 12px 0;
        color: #150C09;
        font-size: 16px;
        font-weight: 600;
      }
      
      .heygg-dev-toolbar__experiment {
        background: #f8f9fa;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 8px;
      }
      
      .heygg-dev-toolbar__experiment:last-child {
        margin-bottom: 0;
      }
      
      .heygg-dev-toolbar__experiment-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
      }
      
      .heygg-dev-toolbar__experiment-name {
        font-weight: 600;
        color: #150C09;
        font-size: 13px;
      }
      
      .heygg-dev-toolbar__experiment-status {
        font-size: 11px;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 500;
      }
      
      .heygg-dev-toolbar__experiment-status--active {
        background: rgba(145, 214, 125, 0.2);
        color: #4a7c3c;
      }
      
      .heygg-dev-toolbar__experiment-status--inactive {
        background: #f8d7da;
        color: #721c24;
      }
      
      .heygg-dev-toolbar__current-variant {
        font-size: 12px;
        color: #666;
        margin-bottom: 8px;
      }
      
      .heygg-dev-toolbar__variant-buttons {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
      }
      
      .heygg-dev-toolbar__variant-btn {
        padding: 4px 8px;
        border: 1px solid #689BE8;
        background: white;
        color: #689BE8;
        border-radius: 4px;
        cursor: pointer;
        font-size: 11px;
        transition: all 0.2s ease;
      }
      
      .heygg-dev-toolbar__variant-btn:hover {
        background: #689BE8;
        color: white;
      }
      
      .heygg-dev-toolbar__variant-btn--current {
        background: #689BE8;
        color: white;
      }
      
      .heygg-dev-toolbar__button {
        padding: 8px 16px;
        border: 1px solid #689BE8;
        background: #689BE8;
        color: white;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 500;
        transition: all 0.2s ease;
      }
      
      .heygg-dev-toolbar__button:hover {
        background: #5080d6;
        border-color: #5080d6;
      }
      
      .heygg-dev-toolbar__button--danger {
        background: #FF6741;
        border-color: #FF6741;
      }
      
      .heygg-dev-toolbar__button--danger:hover {
        background: #e5522a;
        border-color: #e5522a;
      }
      
      .heygg-dev-toolbar__loading {
        text-align: center;
        color: #666;
        padding: 20px;
      }
      
      .heygg-dev-toolbar__empty {
        text-align: center;
        color: #999;
        padding: 20px;
        font-style: italic;
      }
      
      .heygg-dev-toolbar__info-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px solid #f0f0f0;
      }
      
      .heygg-dev-toolbar__info-item:last-child {
        border-bottom: none;
      }
      
      .heygg-dev-toolbar__info-label {
        font-weight: 500;
        color: #666;
        font-size: 12px;
      }
      
      .heygg-dev-toolbar__info-value {
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 11px;
        color: #150C09;
        background: #f8f9fa;
        padding: 2px 6px;
        border-radius: 3px;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .heygg-dev-toolbar__info-value--success {
        background: rgba(145, 214, 125, 0.2);
        color: #4a7c3c;
      }
      
      .heygg-dev-toolbar__info-value--warning {
        background: rgba(255, 103, 65, 0.15);
        color: #b54520;
      }

      /* Storage table styles */
      .heygg-dev-toolbar__storage-table {
        margin-top: 8px;
      }

      .heygg-dev-toolbar__table {
        width: 100%;
        border-collapse: collapse;
        background: #f8f8f8;
        border-radius: 4px;
        overflow: hidden;
        border: 1px solid #e0e0e0;
      }

      .heygg-dev-toolbar__table th,
      .heygg-dev-toolbar__table td {
        text-align: left;
        padding: 6px 10px;
        border-bottom: 1px solid #e8e8e8;
      }

      .heygg-dev-toolbar__table th {
        background: #f0f0f0;
        font-weight: 600;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #333333;
        position: sticky;
        top: 0;
        z-index: 1;
      }

      .heygg-dev-toolbar__table tbody tr:hover {
        background: #f0f0f0;
      }

      .heygg-dev-toolbar__table tbody tr:last-child td {
        border-bottom: none;
      }

      .heygg-dev-toolbar__key {
        font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Droid Sans Mono', monospace;
        font-weight: 600;
        color: #2c3e50;
        width: 40%;
        min-width: 180px;
        white-space: nowrap;
        font-size: 12px;
      }

      .heygg-dev-toolbar__value {
        font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Droid Sans Mono', monospace;
        color: #555555;
        width: 60%;
        word-break: break-word;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 350px;
        font-size: 12px;
        line-height: 1.3;
      }

      .heygg-dev-toolbar__value--expanded {
        white-space: pre-wrap;
        overflow: visible;
        text-overflow: unset;
        max-width: none;
        background: #f0f0f0;
        padding: 6px;
        border-radius: 3px;
        margin: 2px 0;
      }

      .heygg-dev-toolbar__value[data-full-value]:not([data-full-value=""]) {
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
      }

      .heygg-dev-toolbar__value[data-full-value]:not([data-full-value=""]):hover {
        background: #e8e8e8;
        color: #333333;
      }


      .heygg-dev-toolbar__null {
        color: #999;
        font-style: italic;
        font-weight: 500;
      }

      .heygg-dev-toolbar__empty {
        text-align: center;
        color: #666;
        font-style: italic;
        padding: 20px;
        font-size: 13px;
      }

      /* Code highlighting and copy button styles */
      .heygg-dev-toolbar__code-container,
      .heygg-dev-toolbar__value-container {
        position: relative;
        padding-top: 20px;
        margin: 2px 0;
      }

      .heygg-dev-toolbar__copy-btn {
        position: absolute;
        top: 2px;
        right: 2px;
        background: #f0f0f0;
        border: 1px solid #ddd;
        border-radius: 3px;
        padding: 2px 6px;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.2s ease;
        z-index: 2;
      }

      .heygg-dev-toolbar__copy-btn:hover {
        background: #e0e0e0;
        border-color: #ccc;
      }

      .heygg-dev-toolbar__hover-copy-btn {
        position: absolute;
        top: 50%;
        right: 4px;
        transform: translateY(-50%);
        background: #f0f0f0;
        border: 1px solid #ddd;
        border-radius: 3px;
        padding: 1px 4px;
        font-size: 10px;
        cursor: pointer;
        transition: all 0.2s ease;
        z-index: 2;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .heygg-dev-toolbar__hover-copy-btn:hover {
        background: #e0e0e0;
        border-color: #ccc;
        transform: translateY(-50%) scale(1.1);
      }

      .heygg-dev-toolbar__code {
        margin: 0;
        padding: 6px 8px;
        background: #f8f8f8;
        border: 1px solid #e0e0e0;
        border-radius: 3px;
        font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Droid Sans Mono', monospace;
        font-size: 11px;
        line-height: 1.3;
        white-space: pre-wrap;
        overflow: auto;
        max-height: 300px;
      }

      .heygg-dev-toolbar__raw-value {
        display: block;
        padding: 8px;
        background: #f8f8f8;
        border: 1px solid #e0e0e0;
        border-radius: 3px;
        font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', 'Droid Sans Mono', monospace;
        font-size: 11px;
        line-height: 1.4;
        white-space: pre-wrap;
        word-break: break-word;
      }

    `;
    
    document.head.appendChild(style);
  }

  /**
   * Add event listeners to toolbar elements
   */
  private addEventListeners() {
    // Toggle button
    const toggle = document.getElementById('heygg-dev-toolbar-toggle');
    toggle?.addEventListener('click', () => {
      this.togglePanel();
    });

    // Tab switching
    const tabs = document.querySelectorAll('.heygg-dev-toolbar__tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const tabName = target.getAttribute('data-tab');
        if (tabName) {
          this.switchTab(tabName);
        }
      });
    });

    // Reset storage button
    const resetBtn = document.getElementById('reset-storage-btn');
    resetBtn?.addEventListener('click', () => {
      this.resetLocalStorage();
    });

    // Close panel when clicking outside
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.heygg-dev-toolbar') && this.isExpanded) {
        this.togglePanel();
      }
    });
  }

  /**
   * Toggle the expanded panel
   */
  private togglePanel() {
    const panel = document.getElementById('heygg-dev-toolbar-panel');
    if (panel) {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        panel.classList.add('heygg-dev-toolbar__panel--expanded');
      } else {
        panel.classList.remove('heygg-dev-toolbar__panel--expanded');
      }
    }
  }

  /**
   * Switch between tabs
   */
  private switchTab(tabName: string) {
    // Update tab buttons
    const tabs = document.querySelectorAll('.heygg-dev-toolbar__tab');
    tabs.forEach(tab => {
      tab.classList.remove('heygg-dev-toolbar__tab--active');
    });
    
    const activeTab = document.querySelector(`[data-tab="${tabName}"]`);
    activeTab?.classList.add('heygg-dev-toolbar__tab--active');

    // Update tab content
    const contents = document.querySelectorAll('.heygg-dev-toolbar__tab-content');
    contents.forEach(content => {
      content.classList.remove('heygg-dev-toolbar__tab-content--active');
    });
    
    const activeContent = document.getElementById(`tab-${tabName}`);
    activeContent?.classList.add('heygg-dev-toolbar__tab-content--active');

    // Refresh storage table when switching to storage tab
    if (tabName === 'storage') {
      this.updateStorageTable();
    }
  }

  /**
   * Load experiments and their current variants
   */
  private async loadExperiments() {
    // Check if user is authenticated before making API calls
    const hasJWT = !!localStorage.getItem('heygg_jwt');
    
    if (!hasJWT) {
      console.log('[DevToolbar] User not authenticated (no JWT token), skipping experiment loading');
      this.experiments = [];
      this.renderEmptyExperiments();
      return;
    }

    try {
      console.log('[DevToolbar] Loading all experiments from API');
      
      // Get all experiments from the API for this game
      const apiExperiments = await window.HeyGoodGame.ABTesting.getAllExperiments(this.config.gameKey!);
      
      const experiments: ExperimentInfo[] = [];
      
      for (const apiExp of apiExperiments) {
        try {
          // Use experiment name as the unique identifier
          const experimentKey = apiExp.name;
          
          // Get current variant for this experiment
          const variant = await window.HeyGoodGame.ABTesting.getVariant(experimentKey, 'default');
          
          experiments.push({
            key: experimentKey,
            currentVariant: variant.name,
            availableVariants: apiExp.variants.map(v => v.name),
            status: apiExp.status === 'active' ? 'active' : 'inactive',
          });
        } catch (error) {
          const experimentKey = apiExp.name;
          console.warn(`[DevToolbar] Failed to get current variant for ${experimentKey}:`, error);
          experiments.push({
            key: experimentKey,
            currentVariant: 'unknown',
            availableVariants: apiExp.variants.map(v => v.name),
            status: apiExp.status === 'active' ? 'active' : 'inactive',
          });
        }
      }
      
      // Fallback to manually configured experiments if API returns empty
      if (experiments.length === 0 && this.config.experiments && this.config.experiments.length > 0) {
        console.log('[DevToolbar] No experiments from API, using configured experiments:', this.config.experiments);
        
        for (const key of this.config.experiments) {
          try {
            const variant = await window.HeyGoodGame.ABTesting.getVariant(key, 'default');
            
            experiments.push({
              key,
              currentVariant: variant.name,
              availableVariants: this.getKnownVariants(key),
              status: 'active',
            });
          } catch (error) {
            console.warn(`[DevToolbar] Failed to load experiment ${key}:`, error);
            experiments.push({
              key,
              currentVariant: 'unknown',
              availableVariants: this.getKnownVariants(key),
              status: 'inactive',
            });
          }
        }
      }
      
      this.experiments = experiments;
      this.renderExperiments();
      
    } catch (error) {
      console.error('[DevToolbar] Failed to load experiments from API:', error);
      
      // If API call fails due to authentication, show empty experiments
      if (error && (error.toString().includes('401') || error.toString().includes('403') || error.toString().includes('Unauthorized'))) {
        console.log('[DevToolbar] Authentication error detected, showing empty experiments');
        this.experiments = [];
        this.renderEmptyExperiments();
      } else {
        // For other errors, still try to show empty experiments
        this.renderEmptyExperiments();
      }
    }
  }

  /**
   * Get known variants for an experiment (can be extended by games)
   */
  private getKnownVariants(_experimentKey: string): string[] {
    // Default variants - games can extend this by calling setExperimentVariants
    return ['default', 'control', 'treatment', 'variant_a', 'variant_b'];
  }

  /**
   * Render the experiments list
   */
  private renderExperiments() {
    const container = document.getElementById('experiments-list');
    if (!container) return;

    if (this.experiments.length === 0) {
      this.renderEmptyExperiments();
      return;
    }

    container.innerHTML = this.experiments.map(exp => `
      <div class="heygg-dev-toolbar__experiment">
        <div class="heygg-dev-toolbar__experiment-header">
          <div class="heygg-dev-toolbar__experiment-name">${exp.key}</div>
          <div class="heygg-dev-toolbar__experiment-status heygg-dev-toolbar__experiment-status--${exp.status}">
            ${exp.status}
          </div>
        </div>
        <div class="heygg-dev-toolbar__current-variant">
          Current: <strong>${exp.currentVariant}</strong>
        </div>
        <div class="heygg-dev-toolbar__variant-buttons">
          ${(exp.availableVariants || []).map(variant => `
            <button 
              class="heygg-dev-toolbar__variant-btn ${variant === exp.currentVariant ? 'heygg-dev-toolbar__variant-btn--current' : ''}"
              data-experiment="${exp.key}"
              data-variant="${variant}"
            >
              ${variant}
            </button>
          `).join('')}
        </div>
      </div>
    `).join('');

    // Add event listeners for variant buttons
    const variantButtons = container.querySelectorAll('.heygg-dev-toolbar__variant-btn');
    variantButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const experiment = target.getAttribute('data-experiment');
        const variant = target.getAttribute('data-variant');
        if (experiment && variant) {
          this.switchToVariant(experiment, variant);
        }
      });
    });
  }

  /**
   * Render empty experiments state
   */
  private renderEmptyExperiments() {
    const container = document.getElementById('experiments-list');
    if (container) {
      container.innerHTML = '<div class="heygg-dev-toolbar__empty">No experiments configured</div>';
    }
  }

  /**
   * Switch to a specific variant
   */
  private switchToVariant(experiment: string, variant: string) {
    console.log(`[DevToolbar] Switching to variant ${variant} for experiment ${experiment}`);
    
    // Build URL with variant override
    const url = new URL(window.location.href);
    url.searchParams.set(`ab[${experiment}]`, variant);
    
    // Reload page with the new URL
    window.location.href = url.toString();
  }

  /**
   * Reset local storage and refresh page
   */
  private resetLocalStorage() {
    console.log('[DevToolbar] Resetting local storage');
    
    // Use custom callback if provided
    if (this.config.localStorageResetCallback) {
      this.config.localStorageResetCallback();
      return;
    }

    // Otherwise, clear specified keys
    const keysToRemove = this.config.localStorageKeys || [];
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`[DevToolbar] Removed localStorage key: ${key}`);
    });

    // Refresh the page
    window.location.reload();
  }

  /**
   * Allow games to set known variants for experiments
   */
  public setExperimentVariants(experimentKey: string, variants: string[]) {
    const experiment = this.experiments.find(exp => exp.key === experimentKey);
    if (experiment) {
      experiment.availableVariants = variants;
      this.renderExperiments();
    }
  }

  /**
   * Add additional local storage keys to reset
   */
  public addLocalStorageKeys(keys: string[]) {
    this.config.localStorageKeys = [...(this.config.localStorageKeys || []), ...keys];
  }

  /**
   * Update the info panel with current environment details
   */
  private updateInfoPanel() {
    // Update API base URL
    const apiBaseUrlEl = document.getElementById('api-base-url-value');
    if (apiBaseUrlEl) {
      const apiBaseUrl = window.HeyGoodGame?.config?.apiBaseUrl || 'https://play.hey.gg';
      apiBaseUrlEl.textContent = apiBaseUrl;
      
      // Add visual indicator for different environments
      if (apiBaseUrl.includes('localhost') || apiBaseUrl.includes('127.0.0.1')) {
        apiBaseUrlEl.className = 'heygg-dev-toolbar__info-value heygg-dev-toolbar__info-value--warning';
      } else if (apiBaseUrl.includes('dev.') || apiBaseUrl.includes('staging.')) {
        apiBaseUrlEl.className = 'heygg-dev-toolbar__info-value heygg-dev-toolbar__info-value--warning';
      } else {
        apiBaseUrlEl.className = 'heygg-dev-toolbar__info-value heygg-dev-toolbar__info-value--success';
      }
    }
    
    // Update auth status
    const authStatusEl = document.getElementById('auth-status-value');
    if (authStatusEl) {
      const hasJWT = !!localStorage.getItem('heygg_jwt');
      const hasAnonymousId = !!localStorage.getItem('heygg_anonymous_id');
      
      if (hasJWT) {
        authStatusEl.textContent = 'Authenticated';
        authStatusEl.className = 'heygg-dev-toolbar__info-value heygg-dev-toolbar__info-value--success';
      } else if (hasAnonymousId) {
        authStatusEl.textContent = 'Anonymous';
        authStatusEl.className = 'heygg-dev-toolbar__info-value heygg-dev-toolbar__info-value--warning';
      } else {
        authStatusEl.textContent = 'None';
        authStatusEl.className = 'heygg-dev-toolbar__info-value';
      }
    }
  }

  /**
   * Update the storage table with localStorage keys and values
   */
  private updateStorageTable() {
    const storageTableEl = document.getElementById('storage-keys-table');
    if (!storageTableEl) return;

    const keys = this.config.localStorageKeys || [];
    
    if (keys.length === 0) {
      storageTableEl.innerHTML = '<div class="heygg-dev-toolbar__empty">No localStorage keys configured</div>';
      return;
    }

    // Create table HTML
    let tableHTML = `
      <table class="heygg-dev-toolbar__table">
        <thead>
          <tr>
            <th>Key</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
    `;

    keys.forEach(key => {
      const value = localStorage.getItem(key);
      const escapeHtml = (str: string) => {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      };
      
      const displayValue = value ? escapeHtml(value) : '<span class="heygg-dev-toolbar__null">null</span>';
      const truncatedValue = value && value.length > 50 
        ? escapeHtml(value.substring(0, 50)) + '...' 
        : displayValue;

      tableHTML += `
        <tr>
          <td class="heygg-dev-toolbar__key">${escapeHtml(key)}</td>
          <td class="heygg-dev-toolbar__value" data-full-value="${value ? encodeURIComponent(value) : ''}" title="${value && value.length > 50 ? 'Click to expand' : ''}">
            ${truncatedValue}
          </td>
        </tr>
      `;
    });

    tableHTML += `
        </tbody>
      </table>
    `;

    storageTableEl.innerHTML = tableHTML;

    // Add click handlers for expandable values and copy functionality for short values
    const valueElements = storageTableEl.querySelectorAll('.heygg-dev-toolbar__value');
    valueElements.forEach(el => {
      const element = el as HTMLElement;
      const fullValue = element.dataset.fullValue;
      
      if (fullValue) {
        const decodedValue = decodeURIComponent(fullValue);
        
        if (decodedValue.length > 50) {
          // Long values: expandable
          element.style.cursor = 'pointer';
          element.addEventListener('click', (e) => {
            // Don't toggle if clicking on copy button
            if ((e.target as HTMLElement).classList.contains('heygg-dev-toolbar__copy-btn')) {
              return;
            }
          
          const escapeHtml = (str: string) => {
            const div = document.createElement('div');
            div.textContent = str;
            return div.innerHTML;
          };
          
          const formatJson = (str: string) => {
            try {
              const parsed = JSON.parse(str);
              return JSON.stringify(parsed, null, 2);
            } catch {
              return str;
            }
          };
          
          const detectContentType = (str: string) => {
            // Try JSON first
            try {
              JSON.parse(str);
              return 'json';
            } catch {}
            
            // Check if it looks like HTML
            if (str.trim().startsWith('<') && str.trim().endsWith('>')) {
              return 'html';
            }
            
            return 'text';
          };
          
          const highlightContent = (content: string, type: string) => {
            try {
              if (type === 'json') {
                return hljs.highlight(content, { language: 'json' }).value;
              } else if (type === 'html') {
                return hljs.highlight(content, { language: 'html' }).value;
              } else {
                return escapeHtml(content);
              }
            } catch {
              return escapeHtml(content);
            }
          };
          
          if (element.classList.contains('heygg-dev-toolbar__value--expanded')) {
            // Collapse
            const originalValue = decodeURIComponent(fullValue);
            element.innerHTML = escapeHtml(originalValue.substring(0, 50)) + '...';
            element.classList.remove('heygg-dev-toolbar__value--expanded');
            element.title = 'Click to expand';
          } else {
            // Close all other expanded values first
            valueElements.forEach(otherEl => {
              if (otherEl !== element && otherEl.classList.contains('heygg-dev-toolbar__value--expanded')) {
                const otherFullValue = (otherEl as HTMLElement).dataset.fullValue;
                if (otherFullValue) {
                  const otherOriginalValue = decodeURIComponent(otherFullValue);
                  otherEl.innerHTML = escapeHtml(otherOriginalValue.substring(0, 50)) + '...';
                  otherEl.classList.remove('heygg-dev-toolbar__value--expanded');
                  (otherEl as HTMLElement).title = 'Click to expand';
                }
              }
            });
            
            // Expand current value
            const rawValue = decodeURIComponent(fullValue);
            const contentType = detectContentType(rawValue);
            const formattedValue = contentType === 'json' ? formatJson(rawValue) : rawValue;
            
            if (contentType === 'json' || contentType === 'html') {
              element.innerHTML = `
                <div class="heygg-dev-toolbar__code-container">
                  <button class="heygg-dev-toolbar__copy-btn" title="Copy to clipboard">📋</button>
                  <pre class="heygg-dev-toolbar__code hljs">${highlightContent(formattedValue, contentType)}</pre>
                </div>
              `;
            } else {
              element.innerHTML = `
                <div class="heygg-dev-toolbar__value-container">
                  <button class="heygg-dev-toolbar__copy-btn" title="Copy to clipboard">📋</button>
                  <span class="heygg-dev-toolbar__raw-value">${escapeHtml(formattedValue)}</span>
                </div>
              `;
            }
            
            element.classList.add('heygg-dev-toolbar__value--expanded');
            element.title = 'Click to collapse';
            
            // Add copy functionality
            const copyBtn = element.querySelector('.heygg-dev-toolbar__copy-btn') as HTMLElement;
            if (copyBtn) {
              copyBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                try {
                  await navigator.clipboard.writeText(rawValue);
                  copyBtn.textContent = '✅';
                  setTimeout(() => {
                    copyBtn.textContent = '📋';
                  }, 1000);
                } catch (err) {
                  console.error('Failed to copy to clipboard:', err);
                  copyBtn.textContent = '❌';
                  setTimeout(() => {
                    copyBtn.textContent = '📋';
                  }, 1000);
                }
              });
            }
          }
        });
        } else {
          // Short values: add copy on hover
          element.style.position = 'relative';
          
          // Create copy button that appears on hover
          const copyBtn = document.createElement('button');
          copyBtn.className = 'heygg-dev-toolbar__hover-copy-btn';
          copyBtn.textContent = '📋';
          copyBtn.title = 'Copy to clipboard';
          copyBtn.style.display = 'none';
          element.appendChild(copyBtn);
          
          // Show/hide copy button on hover
          element.addEventListener('mouseenter', () => {
            copyBtn.style.display = 'block';
          });
          
          element.addEventListener('mouseleave', () => {
            copyBtn.style.display = 'none';
          });
          
          // Copy functionality
          copyBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            try {
              await navigator.clipboard.writeText(decodedValue);
              copyBtn.textContent = '✅';
              setTimeout(() => {
                copyBtn.textContent = '📋';
              }, 1000);
            } catch (err) {
              console.error('Failed to copy to clipboard:', err);
              copyBtn.textContent = '❌';
              setTimeout(() => {
                copyBtn.textContent = '📋';
              }, 1000);
            }
          });
        }
      }
    });
  }
}

// Create singleton instance
const devToolbar = new DevToolbar();

export default devToolbar;
